import React, { useState, useRef, useEffect } from "react";
import FileMenu from "./FileMenu";
import ModelingMenu from "./ModelingMenu";
import ViewMenu from "./ViewMenu";
import ConfigurationMenu from "./ConfigurationMenu";
import { UnitConfigDialog } from "../ui/UnitConfigDialog";
import { SaveProjectDialog } from "./dialogs/SaveProjectDialog";
import { LoadProjectDialog } from "./dialogs/LoadProjectDialog";
import { RecentProjectsDialog } from "./dialogs/RecentProjectsDialog";
import { NewProjectDialog } from "./dialogs/NewProjectDialog";
import {
  getCurrentProjectPath,
  setCurrentSessionProject,
  clearCurrentSessionProject,
} from "../../services/currentProject";
import {
  exportAllData,
  clearAllModelData,
} from "../../services/centralizedDataManager";
import { addRecentProject } from "../../services/recentProjects";
import { useSettingsStore } from "../../store/useSettingsStore";
import { useUIStore } from "@/store/useUIStore";
import { useModelStore } from "@/store/useModelStore";
import { usePropertyStore } from "@/store/usePropertyStore";
import { useSupportStore } from "@/store/useSupportStore";
import { useBeamSectionStore } from "@/hooks/useBeamSection";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { TerminalDialog } from "../ui/Terminal";
import {
  getDirectoryHandle,
  saveDirectoryHandle,
} from "../../services/fileHandleStorage";
import { CreateProjectDialog } from "./dialogs/CreateProjectDialog";
import { useUnitStore } from "../../store/useUnitStore";
import { useGlobalSelectionStore } from "@/store/useGlobalSelectionStore";
import { exportToTerminalFormat } from "@/services/terminalFormatter";

const MenuBar: React.FC = () => {
  const isElectron = typeof window !== "undefined" && window.electronAPI;

  const [hoverMenu, setHoverMenu] = useState<string | null>(null);
  const [clickedMenu, setClickedMenu] = useState<string | null>("modeling");
  const [isUnitConfigOpen, setIsUnitConfigOpen] = useState(false);
  const [isNewProjectDialogOpen, setIsNewProjectDialogOpen] = useState(false);
  const [isSaveProjectOpen, setIsSaveProjectOpen] = useState(false);
  const [pendingNewProject, setPendingNewProject] = useState(false);
  const [isLoadProjectOpen, setIsLoadProjectOpen] = useState(false);
  const [isRecentProjectsOpen, setIsRecentProjectsOpen] = useState(false);
  const [isSaveAsDialogOpen, setIsSaveAsDialogOpen] = useState(false);
  const [isTerminalOpen, setIsTerminalOpen] = useState(false);

  useEffect(() => {
    window.openTerminal = () => setIsTerminalOpen(true);
  }, []);

  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [lastSavedState, setLastSavedState] = useState<string | null>(null);
  const [saveNotification, setSaveNotification] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const settingsStore = useSettingsStore();

  const [isCreateProjectDialogOpen, setIsCreateProjectDialogOpen] =
    useState(false);
  const { setLengthUnit, setForceUnit } = useUnitStore();

  const { hasProject, refreshCurrentProject } = useCurrentProject();

  const fileMenuKey = hasProject ? "has-project" : "no-project";

  useEffect(() => {
    // Only clear if you want a completely fresh start
  }, []);

  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      // ESC - Cancel everything and deselect menus
      if (e.key === "Escape") {
        e.preventDefault();

        // Disable all modeling modes
        if (clickedMenu === "modeling") {
          disableAllModes();
        }

        // Deselect menu (but keep Modeling/View as default)
        if (clickedMenu !== "modeling" && clickedMenu !== "view") {
          setClickedMenu("modeling"); // Return to modeling menu
        }
      }

      // Ctrl+S - Quick Save (only if project exists)
      if ((e.ctrlKey || e.metaKey) && e.key === "s" && !e.shiftKey) {
        e.preventDefault();
        if (hasProject) {
          await handleQuickSave();
        } else {
          setIsSaveAsDialogOpen(true);
        }
      }

      // Ctrl+Shift+S - Save As
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "S") {
        e.preventDefault();
        setIsSaveAsDialogOpen(true);
      }

      // Ctrl+O - Open/Load
      if ((e.ctrlKey || e.metaKey) && e.key === "o") {
        e.preventDefault();
        setIsLoadProjectOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasProject, clickedMenu]);

  // NEW PROJECT FUNCTIONS

  // Check if project has data
  const hasProjectData = () => {
    const modelStore = useModelStore.getState();
    return modelStore.nodes.length > 0 || modelStore.beams.length > 0;
  };

  // Check if user has drawn anything OR has a project
  const hasDrawnData = () => {
    const modelStore = useModelStore.getState();
    return modelStore.nodes.length > 0 || modelStore.beams.length > 0;
  };

  // Check if we should enable Save buttons
  const shouldEnableSave = () => {
    return hasProject || hasDrawnData();
  };

  // Get current project name
  const getCurrentProjectName = async () => {
    const path = await getCurrentProjectPath();
    if (path) {
      const pathParts = path.split(/[\\/]/);
      return pathParts.pop() || "Untitled Project";
    }
    return "Untitled Project";
  };

  // Clear all project data using centralized function
  const clearAllProjectData = () => {
    useGlobalSelectionStore.getState().clearCursorMode();
    disableAllModelingModes();

    // Disable all modeling modes FIRST
    // disableAllModes();

    // Use centralized clear function
    clearAllModelData();

    // Clear current project reference
    clearCurrentSessionProject();

    setLastSavedState(null);

    refreshCurrentProject();
  };

  // Handle "New Project" click
  const handleNewProject = () => {
    const hasData = hasProjectData();

    if (hasData || hasProject) {
      setIsNewProjectDialogOpen(true);
    } else {
      setIsCreateProjectDialogOpen(true);
    }
  };

  const handleCreateNewProject = async (config: {
    projectName: string;
    projectLocation: string;
    lengthUnit: any;
    forceUnit: any;
    directoryHandle?: any;
  }) => {
    try {
      const isElectron = typeof window !== "undefined" && window.electronAPI;

      // Set units first
      setLengthUnit(config.lengthUnit);
      setForceUnit(config.forceUnit);

      useModelStore.getState().setProjectName(config.projectName);

      if (isElectron) {
        // Electron: Create project folder and initialize
        const platform = await window.electronAPI.getPlatform();
        const separator = platform === "win32" ? "\\" : "/";
        const projectPath = `${config.projectLocation}${separator}${config.projectName}`;

        // Create empty data and config files
        const emptyData = {
          nodes: [],
          beams: [],
          properties: [],
          supports: [],
          beamSections: {},
          nodeMomentLoads: {},
          nodePerpLoads: {},
        };

        const configData = {
          units: {
            length: config.lengthUnit,
            force: config.forceUnit,
          },
          display: settingsStore.display,
          analysis: settingsStore.analysis,
          performance: settingsStore.performance,
        };

        const dataResult = await window.electronAPI.saveProjectDirect({
          filePath: `${projectPath}${separator}data.fema`,
          content: JSON.stringify(emptyData, null, 2),
        });

        const configResult = await window.electronAPI.saveProjectDirect({
          filePath: `${projectPath}${separator}config.fema`,
          content: JSON.stringify(configData, null, 2),
        });

        if (!dataResult.success || !configResult.success) {
          throw new Error("Failed to create project files");
        }

        // Set as current project
        setCurrentSessionProject(projectPath);
        await addRecentProject(config.projectName, projectPath);

        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("predefined_loads_updated"));
          window.dispatchEvent(
            new CustomEvent("predefined_truss_presets_updated")
          );
        }, 200);

        setIsCreateProjectDialogOpen(false);
        refreshCurrentProject();

        disableAllModelingModes();

        setTimeout(() => {
          alert(`✅ Project "${config.projectName}" created successfully!`);
        }, 100);
      } else {
        // Browser mode: Create project files and store handle
        const supportsFileSystemAccess = "showDirectoryPicker" in window;

        if (supportsFileSystemAccess && config.directoryHandle) {
          // Create empty data and config files
          const emptyData = {
            nodes: [],
            beams: [],
            properties: [],
            supports: [],
            beamSections: {},
            nodeMomentLoads: {},
            nodePerpLoads: {},
          };

          const configData = {
            units: {
              length: config.lengthUnit,
              force: config.forceUnit,
            },
            display: settingsStore.display,
            analysis: settingsStore.analysis,
            performance: settingsStore.performance,
          };

          // Get or create project folder
          const projectFolderHandle =
            await config.directoryHandle.getDirectoryHandle(
              config.projectName,
              { create: true }
            );

          // Save data.fema
          const dataFileHandle = await projectFolderHandle.getFileHandle(
            "data.fema",
            {
              create: true,
            }
          );
          const dataWritable = await dataFileHandle.createWritable();
          await dataWritable.write(JSON.stringify(emptyData, null, 2));
          await dataWritable.close();

          // Save config.fema
          const configFileHandle = await projectFolderHandle.getFileHandle(
            "config.fema",
            {
              create: true,
            }
          );
          const configWritable = await configFileHandle.createWritable();
          await configWritable.write(JSON.stringify(configData, null, 2));
          await configWritable.close();

          // Create unique ID for browser storage
          // const uniqueId = `web-${Date.now()}-${config.projectName}`;

          const projectPath = `${config.directoryHandle.name}/${config.projectName}`;

          // Store BOTH project handle AND parent handle
          await saveDirectoryHandle(projectPath, projectFolderHandle);

          // Set session to unique ID
          setCurrentSessionProject(projectPath);

          // Add to recent projects
          await addRecentProject(config.projectName, projectPath);

          setIsCreateProjectDialogOpen(false);
          refreshCurrentProject();

          disableAllModelingModes();

          setTimeout(() => {
            alert(`✅ Project "${config.projectName}" created successfully!`);
          }, 100);
        } else {
          // No file system access - just clear and set units
          clearAllProjectData();

          disableAllModelingModes();

          setIsCreateProjectDialogOpen(false);

          setTimeout(() => {
            alert(
              `✅ Project "${config.projectName}" created! Note: Save the project to add it to recent projects.`
            );
          }, 100);
        }
      }
    } catch (error) {
      console.error("Failed to create project:", error);
      alert(`Failed to create project: ${error}`);
    }
  };

  // Function to disable all modeling modes
  const disableAllModelingModes = () => {
    const buttons = document.querySelectorAll("button");
    const modesToDisable = [
      "Create Node",
      "Node",
      "Create Beam",
      "Beam",
      "Prep Mode",
    ];

    for (const mode of modesToDisable) {
      const button = Array.from(buttons).find((btn) => {
        const btnText = btn.textContent?.trim();
        const isModelingButton = btn.closest('[class*="bg-gray-8"]') !== null;
        if (!isModelingButton) return false;
        return btnText === mode || btnText?.includes(mode);
      });

      if (button && button.getAttribute("aria-pressed") === "true") {
        button.click();
      }
    }

    const modelStore = useModelStore.getState();
    if (modelStore.isPrepMode) {
      modelStore.cancelPrepMode();
    }
    if (modelStore.isDivideBeamMode) {
      modelStore.cancelDivideBeamMode();
    }
    if (modelStore.isBeamCreationMode) {
      modelStore.cancelBeamCreation();
    }
    useGlobalSelectionStore.getState().clearCursorMode();
  };

  // Handle "Save & New"
  const handleSaveAndNew = async () => {
    const currentPath = await getCurrentProjectPath();

    if (currentPath) {
      setIsNewProjectDialogOpen(false);

      try {
        await handleQuickSave();

        setTimeout(() => {
          clearAllProjectData();
          setIsCreateProjectDialogOpen(true);
        }, 500);
      } catch (error) {
        console.error("Failed to save before new project:", error);
        alert(`Failed to save: ${error}`);
      }
    } else {
      setIsNewProjectDialogOpen(false);
      setPendingNewProject(true);
      setIsSaveAsDialogOpen(true);
    }
  };

  const getDirectoryPath = async (
    dirHandle: FileSystemDirectoryHandle
  ): Promise<string> => {
    try {
      if ("resolve" in dirHandle) {
        const path = await (dirHandle as any).resolve();
        if (path && path.length > 0) {
          return path.join("/");
        }
      }

      if ("getUniqueId" in dirHandle) {
        const id = await (dirHandle as any).getUniqueId();
        return `browser-fs/${id}`;
      }

      return dirHandle.name;
    } catch (e) {
      console.warn("Could not resolve full path:", e);
      return dirHandle.name;
    }
  };

  const handleSaveAsDialogClose = () => {
    setIsSaveAsDialogOpen(false);
    refreshCurrentProject();

    setTimeout(() => {
      const modelStore = useModelStore.getState();
      const propertyStore = usePropertyStore.getState();
      const supportStore = useSupportStore.getState();
      const uiStore = useUIStore.getState();
      const beamSectionStore = useBeamSectionStore.getState();

      const state = JSON.stringify({
        nodes: modelStore.nodes,
        beams: modelStore.beams,
        properties: propertyStore.properties,
        supports: supportStore.supports,
        beamSections: beamSectionStore.sections,
        nodeMomentLoads: uiStore.nodeMomentLoads,
        nodePerpLoads: uiStore.nodePerpLoads,
        predefinedLoads: localStorage.getItem("predefinedloads"),
        predefinedTrussPresets: localStorage.getItem("predefinedtrusspresets"),
      });
      setLastSavedState(state);
    }, 100);
  };

  // Handle "Discard & New"
  const handleDiscardAndNew = () => {
    setIsNewProjectDialogOpen(false);

    disableAllModelingModes();

    clearAllProjectData();
    setIsCreateProjectDialogOpen(true);
  };

  // Handle "Cancel"
  const handleCancelNewProject = () => {
    setIsNewProjectDialogOpen(false);
  };

  // hover menus
  const handleMouseEnter = (menuName: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setHoverMenu(menuName);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setHoverMenu(null);
    }, 200);
  };

  const cancelClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  };

  // Function to close hover menu immediately
  const closeHoverMenu = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setHoverMenu(null);
  };

  // Function to open unit config dialog
  const openUnitConfig = () => {
    setIsUnitConfigOpen(true);
  };

  // click menus
  const handleClick = (menuName: string) => {
    if (clickedMenu === menuName) {
      return;
    }

    if (clickedMenu === "modeling" && menuName !== "modeling") {
      disableAllModes();
    }

    setClickedMenu(menuName);
  };

  const closeClickedMenu = () => {
    if (clickedMenu === "modeling" || clickedMenu === "view") {
      return;
    }

    if (clickedMenu === "modeling") {
      disableAllModes();
    }
    setClickedMenu(null);
  };

  const disableAllModes = () => {
    const buttons = document.querySelectorAll("button");
    const modesToDisable = [
      "Create Node",
      "Node",
      "Create Beam",
      "Beam",
      "Prep Mode",
      "Divide Beam",
    ];

    for (const mode of modesToDisable) {
      const button = Array.from(buttons).find((btn) => {
        const btnText = btn.textContent?.trim();
        const isModelingButton = btn.closest('[class*="bg-gray-8"]') !== null;
        if (!isModelingButton) return false;
        return btnText === mode || btnText?.includes(mode);
      });

      if (button && button.getAttribute("aria-pressed") === "true") {
        button.click();
      }
    }

    const modelStore = useModelStore.getState();
    if (modelStore.isDivideBeamMode) {
      modelStore.cancelDivideBeamMode();
    }
    if (modelStore.isPrepMode) {
      modelStore.cancelPrepMode();
    }
    if (modelStore.isBeamCreationMode) {
      modelStore.cancelBeamCreation();
    }

    // Clear cursor mode
    useGlobalSelectionStore.getState().clearCursorMode();
  };

  const hasUnsavedChanges = () => {
    const modelStore = useModelStore.getState();
    const propertyStore = usePropertyStore.getState();
    const supportStore = useSupportStore.getState();
    const uiStore = useUIStore.getState();
    const beamSectionStore = useBeamSectionStore.getState();

    // Get current state of ALL stores
    const currentState = JSON.stringify({
      nodes: modelStore.nodes,
      beams: modelStore.beams,
      properties: propertyStore.properties,
      supports: supportStore.supports,
      beamSections: beamSectionStore.sections,
      nodeMomentLoads: uiStore.nodeMomentLoads,
      nodePerpLoads: uiStore.nodePerpLoads,
      predefinedLoads: localStorage.getItem("predefinedloads"),
      predefinedTrussPresets: localStorage.getItem("predefinedtrusspresets"),
    });

    // If we have a saved state, compare with current
    if (lastSavedState) {
      return currentState !== lastSavedState;
    }

    // No saved state but has ANY data = unsaved changes
    return (
      modelStore.nodes.length > 0 ||
      modelStore.beams.length > 0 ||
      propertyStore.properties.length > 0 ||
      supportStore.supports.length > 0 ||
      Object.keys(beamSectionStore.sections || {}).length > 0 ||
      Object.keys(uiStore.nodeMomentLoads || {}).length > 0 ||
      Object.keys(uiStore.nodePerpLoads || {}).length > 0 ||
      localStorage.getItem("predefinedloads") !== null ||
      localStorage.getItem("predefinedtrusspresets") !== null
    );
  };

  // Helper function for notifications
  const showSaveNotification = (
    message: string,
    type: "success" | "error" = "success"
  ) => {
    setSaveNotification({ message, type });
    setTimeout(() => setSaveNotification(null), 3000);
  };

  const handleQuickSave = async () => {
    const isElectron = typeof window !== "undefined" && window.electronAPI;

    try {
      const currentPath = await getCurrentProjectPath();

      if (!currentPath) {
        setIsSaveAsDialogOpen(true);
        return;
      }

      // USE TERMINAL FORMAT (changed from exportAllData)
      const terminalData = exportToTerminalFormat();

      // Config stays as JSON (for settings only)
      const configData = {
        units: settingsStore.units,
        display: settingsStore.display,
        analysis: settingsStore.analysis,
        performance: settingsStore.performance,
      };

      if (isElectron) {
        const platform = await window.electronAPI.getPlatform();
        const separator = platform === "win32" ? "\\" : "/";
        const dataPath = `${currentPath}${separator}data.fema`;
        const configPath = `${currentPath}${separator}config.fema`;

        //  Save as TERMINAL FORMAT TEXT (not JSON)
        const dataResult = await window.electronAPI.saveProjectDirect({
          filePath: dataPath,
          content: terminalData, // Pure text, not JSON
        });

        if (!dataResult.success) {
          throw new Error(dataResult.error || "Failed to save data.fema");
        }

        // Save config.fema as JSON (settings only)
        const configResult = await window.electronAPI.saveProjectDirect({
          filePath: configPath,
          content: JSON.stringify(configData, null, 2),
        });

        if (!configResult.success) {
          throw new Error(configResult.error || "Failed to save config.fema");
        }

        const projectName = currentPath.split(/[\\/]/).pop() || "Project";
        await addRecentProject(projectName, currentPath);
        setCurrentSessionProject(currentPath);
      } else {
        // Browser mode
        const dirHandle = await getDirectoryHandle(currentPath);

        if (!dirHandle) {
          showSaveNotification(
            "Permission required. Please select the project folder again.",
            "error"
          );
          setIsSaveAsDialogOpen(true);
          return;
        }

        const pathParts = currentPath.split("/");
        const projectName = pathParts[pathParts.length - 1];

        // Save data.fema as TERMINAL FORMAT TEXT
        const dataFileHandle = await dirHandle.getFileHandle("data.fema", {
          create: true,
        });
        const dataWritable = await dataFileHandle.createWritable();
        await dataWritable.write(terminalData); // Pure text
        await dataWritable.close();

        console.log(" Browser: data.fema saved in terminal format");

        // Save config.fema as JSON
        const configFileHandle = await dirHandle.getFileHandle("config.fema", {
          create: true,
        });
        const configWritable = await configFileHandle.createWritable();
        await configWritable.write(JSON.stringify(configData, null, 2));
        await configWritable.close();

        await addRecentProject(projectName, currentPath);
        setCurrentSessionProject(currentPath);
      }

      // Update last saved state
      const modelStore = useModelStore.getState();
      const propertyStore = usePropertyStore.getState();
      const supportStore = useSupportStore.getState();
      const uiStore = useUIStore.getState();
      const beamSectionStore = useBeamSectionStore.getState();

      const state = JSON.stringify({
        nodes: modelStore.nodes,
        beams: modelStore.beams,
        properties: propertyStore.properties,
        supports: supportStore.supports,
        beamSections: beamSectionStore.sections,
        nodeMomentLoads: uiStore.nodeMomentLoads,
        nodePerpLoads: uiStore.nodePerpLoads,
        predefinedLoads: localStorage.getItem("predefinedloads"),
        predefinedTrussPresets: localStorage.getItem("predefinedtrusspresets"),
      });
      setLastSavedState(state);

      // Show success notification
      const projectName = currentPath.split(/[\\/]/).pop() || "Project";
      showSaveNotification(`Project "${projectName}" saved successfully!`);
    } catch (error: any) {
      console.error("Quick Save failed:", error);
      showSaveNotification(`Failed to save project: ${error}`, "error");
    }
  };

  // Save Project Handler
  const openSaveProject = async () => {
    if (!hasProject) {
      setIsSaveAsDialogOpen(true);
      return;
    }

    await handleQuickSave();
  };

  // Save As Handler
  const openSaveProjectAs = () => {
    setIsSaveAsDialogOpen(true);
  };

  // Load project
  const openLoadProject = () => {
    setIsLoadProjectOpen(true);
  };

  // Recent project
  const openRecentProjects = () => {
    setIsRecentProjectsOpen(true);
  };

  const handleLoadProjectClose = () => {
    setIsLoadProjectOpen(false);

    disableAllModelingModes();

    refreshCurrentProject();

    setTimeout(() => {
      const modelStore = useModelStore.getState();
      const propertyStore = usePropertyStore.getState();
      const supportStore = useSupportStore.getState();
      const uiStore = useUIStore.getState();
      const beamSectionStore = useBeamSectionStore.getState();

      const state = JSON.stringify({
        nodes: modelStore.nodes,
        beams: modelStore.beams,
        properties: propertyStore.properties,
        supports: supportStore.supports,
        beamSections: beamSectionStore.sections,
        nodeMomentLoads: uiStore.nodeMomentLoads,
        nodePerpLoads: uiStore.nodePerpLoads,
        predefinedLoads: localStorage.getItem("predefinedloads"),
        predefinedTrussPresets: localStorage.getItem("predefinedtrusspresets"),
      });

      setLastSavedState(state);

      window.dispatchEvent(new CustomEvent("predefined_loads_updated"));
      window.dispatchEvent(new CustomEvent("predefined_truss_presets_updated"));
    }, 100);
  };

  const handleRecentProjectsClose = () => {
    setIsRecentProjectsOpen(false);

    disableAllModelingModes();

    refreshCurrentProject();

    setTimeout(() => {
      const modelStore = useModelStore.getState();
      const propertyStore = usePropertyStore.getState();
      const supportStore = useSupportStore.getState();
      const uiStore = useUIStore.getState();
      const beamSectionStore = useBeamSectionStore.getState();

      const state = JSON.stringify({
        nodes: modelStore.nodes,
        beams: modelStore.beams,
        properties: propertyStore.properties,
        supports: supportStore.supports,
        beamSections: beamSectionStore.sections,
        nodeMomentLoads: uiStore.nodeMomentLoads,
        nodePerpLoads: uiStore.nodePerpLoads,
        predefinedLoads: localStorage.getItem("predefinedloads"),
        predefinedTrussPresets: localStorage.getItem("predefinedtrusspresets"),
      });

      setLastSavedState(state);

      window.dispatchEvent(new CustomEvent("predefined_loads_updated"));
      window.dispatchEvent(new CustomEvent("predefined_truss_presets_updated"));
    }, 100);
  };

  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      // Ctrl+S - Quick Save (only if project exists)
      if ((e.ctrlKey || e.metaKey) && e.key === "s" && !e.shiftKey) {
        e.preventDefault();
        if (hasProject) {
          await handleQuickSave();
        } else {
          setIsSaveAsDialogOpen(true);
        }
      }

      // Ctrl+Shift+S - Save As
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "S") {
        e.preventDefault();
        setIsSaveAsDialogOpen(true);
      }

      // Ctrl+O - Open/Load
      if ((e.ctrlKey || e.metaKey) && e.key === "o") {
        e.preventDefault();
        setIsLoadProjectOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasProject]);

  return (
    <>
      {/* Main Menu Bar */}
      <div className="h-9 bg-gray-900 border-b border-gray-700 flex items-center px-3 text-sm">
        {/* File Menu */}
        <div
          className="relative"
          onMouseEnter={() => handleMouseEnter("file")}
          onMouseLeave={handleMouseLeave}
        >
          <button className="px-3 py-1.5 text-gray-300 hover:bg-gray-800 hover:text-white rounded">
            File
          </button>
          {hoverMenu === "file" && (
            <div onMouseEnter={cancelClose} onMouseLeave={handleMouseLeave}>
              <FileMenu
                key={fileMenuKey}
                onMenuItemClick={closeHoverMenu}
                onNewProjectClick={handleNewProject}
                onSaveProjectClick={openSaveProject}
                onSaveProjectAsClick={openSaveProjectAs}
                onLoadProjectClick={openLoadProject}
                onRecentProjectsClick={openRecentProjects}
                hasProject={hasProject}
                hasDrawnData={hasDrawnData()}
              />
            </div>
          )}
        </div>

        {/* Modeling Menu */}
        <button
          onClick={() => handleClick("modeling")}
          className={`px-3 py-1.5 rounded ${
            clickedMenu === "modeling"
              ? "bg-blue-600 text-white"
              : "text-gray-300 hover:bg-gray-800 hover:text-white"
          }`}
        >
          Modeling
        </button>

        {/* View Menu */}
        <button
          onClick={() => handleClick("view")}
          className={`px-3 py-1.5 rounded ${
            clickedMenu === "view"
              ? "bg-blue-600 text-white"
              : "text-gray-300 hover:bg-gray-800 hover:text-white"
          }`}
        >
          View
        </button>

        {/* Configuration Menu */}
        <div
          className="relative"
          onMouseEnter={() => handleMouseEnter("configuration")}
          onMouseLeave={handleMouseLeave}
        >
          <button className="px-3 py-1.5 text-gray-300 hover:bg-gray-800 hover:text-white rounded">
            Configuration
          </button>
          {hoverMenu === "configuration" && (
            <div onMouseEnter={cancelClose} onMouseLeave={handleMouseLeave}>
              <ConfigurationMenu
                onMenuItemClick={closeHoverMenu}
                onUnitConfigClick={openUnitConfig}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modeling sub menu */}
      {clickedMenu === "modeling" && (
        <div className="bg-gray-850 border-b border-gray-700">
          <ModelingMenu />
        </div>
      )}

      {/* View sub menu */}
      {clickedMenu === "view" && (
        <div className="bg-gray-850 border-b border-gray-700">
          <ViewMenu />
        </div>
      )}

      {/* Unit Configuration Dialog */}
      <UnitConfigDialog
        isOpen={isUnitConfigOpen}
        onClose={() => setIsUnitConfigOpen(false)}
      />
      <TerminalDialog
        isOpen={isTerminalOpen}
        onClose={() => setIsTerminalOpen(false)}
      />

      {/* Save Project Dialog */}
      <SaveProjectDialog
        isOpen={isSaveAsDialogOpen}
        onClose={handleSaveAsDialogClose}
        mode="saveAs"
        onSaveComplete={() => {
          if (pendingNewProject) {
            setPendingNewProject(false);

            setTimeout(() => {
              clearAllProjectData();
              setIsCreateProjectDialogOpen(true);
            }, 100);
          }
        }}
      />

      {/* Load Project Dialog */}
      <LoadProjectDialog
        isOpen={isLoadProjectOpen}
        onClose={handleLoadProjectClose}
        hasUnsavedChanges={hasUnsavedChanges}
        onQuickSave={handleQuickSave}
      />

      {/* Recent Projects Dialog */}
      <RecentProjectsDialog
        isOpen={isRecentProjectsOpen}
        onClose={handleRecentProjectsClose}
        hasUnsavedChanges={hasUnsavedChanges}
        onQuickSave={handleQuickSave}
      />

      {/* New Project Dialog */}
      <NewProjectDialog
        isOpen={isNewProjectDialogOpen}
        onSaveAndNew={handleSaveAndNew}
        onDiscardAndNew={handleDiscardAndNew}
        onCancel={handleCancelNewProject}
        projectName={hasProject ? "Current Project" : "Untitled"}
        hasUnsavedChanges={hasUnsavedChanges()}
      />

      {/* CreateProjectDialog */}
      <CreateProjectDialog
        isOpen={isCreateProjectDialogOpen}
        onClose={() => setIsCreateProjectDialogOpen(false)}
        onCreateProject={handleCreateNewProject}
      />

      {/* Toast Notification */}
      {saveNotification && (
        <div
          className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-[9999] flex items-center gap-3 animate-slide-in ${
            saveNotification.type === "success"
              ? "bg-green-600 text-white"
              : "bg-red-600 text-white"
          }`}
        >
          <span className="text-xl">
            {saveNotification.type === "success" ? "" : ""}
          </span>
          <span className="font-medium">{saveNotification.message}</span>
        </div>
      )}
    </>
  );
};

export default MenuBar;
